import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Form, Spinner } from "react-bootstrap";
import {
    FiUpload,
    FiMessageSquare,
    FiCopy,
    FiThumbsUp,
    FiThumbsDown,
    FiRefreshCw,
    FiUser,
} from "react-icons/fi";
import { FaMicrophone, FaPaperPlane } from "react-icons/fa";
import { GrDocumentPdf } from "react-icons/gr";
import { LuBrain } from "react-icons/lu";
import { askQuestion, fetchChats } from "../Services/api";

const Chat = ({ showUpload, setShowUpload, selectedPdfId, readyPdfId, readyPdfName }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [value, setValue] = useState("");
    const [chat, setChat] = useState([]);
    const [asking, setAsking] = useState(false);
    const [typing, setTyping] = useState(false);
    const [err, setErr] = useState("");
    const [copiedIdx, setCopiedIdx] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [interim, setInterim] = useState("");

    const recognitionRef = useRef(null);
    const messagesRef = useRef(null);
    const typingTimerRef = useRef(null);

    useEffect(() => {
        if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
        }
    }, [chat, typing, asking]);

    useEffect(() => {
        return () => clearInterval(typingTimerRef.current);
    }, []);

    useEffect(() => {
        let ignore = false;
        const ctrl = new AbortController();

        async function loadHistory() {
            if (!selectedPdfId) { setChat([]); return; }
            try {
                const res = await fetchChats(selectedPdfId, ctrl.signal);
                if (ignore) return;
                const messages = Array.isArray(res?.messages) ? res.messages : [];
                setChat(messages.map(m => ({ role: m.role, text: m.text })));
                setErr("");
            } catch (e) {
                if (!ignore) setErr(e.message || "Failed to load history");
            }
        }

        loadHistory();
        return () => { ignore = true; ctrl.abort(); };
    }, [selectedPdfId]);


    const isReady =
        Boolean(selectedPdfId) &&
        Boolean(readyPdfId) &&
        String(selectedPdfId) === String(readyPdfId);

    const inputDisabled = !isReady || asking;

    function typeOutToIndex(fullText, targetIndex, { step = 2, delay = 12 } = {}) {
        clearInterval(typingTimerRef.current);
        setTyping(true);
        let i = 0;
        typingTimerRef.current = setInterval(() => {
            i = Math.min(i + step, fullText.length);
            setChat((prev) => {
                const copy = [...prev];
                const msg = copy[targetIndex];
                if (msg && msg.role === "assistant") {
                    copy[targetIndex] = { ...msg, text: fullText.slice(0, i) };
                }
                return copy;
            });
            if (i >= fullText.length) {
                clearInterval(typingTimerRef.current);
                setTyping(false);
            }
        }, delay);
    }

    async function handleSend() {
        if (!value.trim()) return;

        if (!isReady) {
            setErr(
                'Select a PDF and click "Save Source in Vector DB" first (wait for "Embedded Successfully.").'
            );
            return;
        }

        const q = value.trim();
        setErr("");

        const targetIndex = chat.length + 1;

        setChat((c) => [
            ...c,
            { role: "user", text: q },
            { role: "assistant", text: "", pending: true, prompt: q },
        ]);
        setValue("");
        setAsking(true);

        try {
            const res = await askQuestion(selectedPdfId, q);
            const answer = (res?.answer || "").trim();

            setChat((prev) => {
                const copy = [...prev];
                if (copy[targetIndex]) {
                    copy[targetIndex] = { ...copy[targetIndex], pending: false, text: "" };
                }
                return copy;
            });

            typeOutToIndex(answer, targetIndex, { step: 2, delay: 12 });
        } catch (e) {
            setErr(e.message || "Failed to ask");
            setChat((c) => c.slice(0, -1));
        } finally {
            setAsking(false);
        }
    }

    async function handleCopy(idx) {
        try {
            const text = chat[idx]?.text || "";
            await navigator.clipboard.writeText(text);
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx(null), 1200);
        } catch { }
    }

    async function handleRegenerate(idx) {
        const msg = chat[idx];
        if (!msg || msg.role !== "assistant") return;

        let prompt = msg.prompt;
        if (!prompt) {
            for (let i = idx - 1; i >= 0; i--) {
                if (chat[i].role === "user") {
                    prompt = chat[i].text;
                    break;
                }
            }
        }
        if (!prompt) return;

        setErr("");
        setAsking(true);

        setChat((prev) => {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], pending: true, text: "", prompt };
            return copy;
        });

        try {
            const res = await askQuestion(selectedPdfId, prompt);
            const answer = (res?.answer || "").trim();

            setChat((prev) => {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], pending: false, text: "" };
                return copy;
            });

            typeOutToIndex(answer, idx, { step: 2, delay: 12 });
        } catch (e) {
            setErr(e.message || "Failed to regenerate");
            setChat((prev) => {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], pending: false };
                return copy;
            });
        } finally {
            setAsking(false);
        }
    }

    const Avatar = ({ role }) => (
        <div
            className="rounded-circle d-flex align-items-center justify-content-center bg-white border me-2"
            style={{ width: 28, height: 28 }}
        >
            {role === "user" ? <FiUser size={16} /> : <LuBrain size={16} />}
        </div>
    );

    const startMic = () => {
        const r = recognitionRef.current;
        if (!r) { setErr("Speech recognition not supported in this browser."); return; }
        try { r.start(); } catch { }
    };

    const stopMic = () => {
        try { recognitionRef.current?.stop(); } catch { }
    };

    const toggleMic = () => {
        if (!recognitionRef.current) {
            setErr("Speech recognition not supported in this browser.");
            return;
        }
        if (isRecording) stopMic(); else startMic();
    };

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) return;

        const recog = new SR();
        recog.continuous = true;
        recog.interimResults = true;
        recog.lang = "en-US";

        recog.onstart = () => {
            setIsRecording(true);
            setErr("");
        };

        recog.onresult = (e) => {
            let interimText = "";
            let finalText = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const chunk = e.results[i][0].transcript;
                if (e.results[i].isFinal) finalText += chunk;
                else interimText += chunk;
            }
            if (finalText) {
                setValue((prev) => (prev ? prev + " " : "") + finalText.trim());
            }
            setInterim(interimText);
        };

        recog.onerror = (ev) => {
            setErr(ev?.error ? `Mic error: ${ev.error}` : "Mic error");
        };

        recog.onend = () => {
            setIsRecording(false);
            setInterim("");
        };

        recognitionRef.current = recog;

        return () => {
            try { recog.stop(); } catch { }
            recognitionRef.current = null;
        };
    }, []);

    return (
        <div className="p-3">
            <Card
                className="chatCard_css d-flex flex-column"
                style={{ height: "calc(100dvh - 98px)" }}
            >
                <Card.Header
                    className="d-flex align-items-center border-bottom"
                    style={{ padding: 16, flex: "0 0 auto" }}
                >
                    <FiMessageSquare className="text-dark fs_18 fw-bold me-2" />
                    <h5 className="mb-0 fs_18 fw-bold">Chat</h5>
                </Card.Header>

                <Card.Body
                    className="p-3 d-flex flex-column gap-3"
                    style={{ flex: "1 1 auto", overflow: "hidden" }}
                >
                    <div
                        ref={messagesRef}
                        className={`h-100 ${chat.length === 0
                            ? "d-flex align-items-center justify-content-center"
                            : ""
                            }`}
                        style={{ overflowY: "auto" }}
                    >
                        {chat.length === 0 ? (
                            isReady ? (
                                <div className="text-center">
                                    <div
                                        className="d-inline-flex justify-content-center align-items-center rounded-circle border-0 mb-2"
                                        style={{
                                            width: 60,
                                            height: 60,
                                            backgroundColor: "#f0f2ff",
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <GrDocumentPdf size={28} />
                                    </div>
                                    <p className="mb-0 fw-bold">{readyPdfName || "Selected PDF"}</p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <button
                                        className="d-inline-flex justify-content-center align-items-center rounded-circle border-0 mb-2"
                                        style={{
                                            width: 60,
                                            height: 60,
                                            backgroundColor: "#f0f2ff",
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <FiUpload size={28} color="#0a0a0bff" />
                                    </button>
                                    <p className="text-muted mb-2">Start asking about your PDF</p>
                                    <button className="btn-chip" disabled={isUploading} onClick={setShowUpload}>
                                        {isUploading ? "Uploading..." : "Upload a source"}
                                    </button>
                                </div>
                            )
                        ) : (
                            <div className="d-flex flex-column gap-3 w-100">
                                {chat.map((m, i) => (
                                    <div key={i} className="d-flex align-items-start">
                                        <Avatar role={m.role} />
                                        <div
                                            className={`flex-grow-1 p-2 rounded ${m.role === "user" ? "bg-light" : "bg-white border"
                                                }`}
                                        >
                                            <div className="small text-muted mb-1">
                                                {m.role === "user" ? "You" : "Assistant"}
                                            </div>

                                            {m.role === "assistant" && m.pending && !m.text ? (
                                                <div className="d-flex align-items-center">
                                                    <Spinner animation="grow" size="sm" className="me-2" />
                                                    <Spinner animation="grow" size="sm" className="me-2" />
                                                    <Spinner animation="grow" size="sm" />
                                                </div>
                                            ) : (
                                                <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                                    {m.text}
                                                    {typing &&
                                                        i === chat.length - 1 &&
                                                        m.role === "assistant" &&
                                                        !m.pending
                                                        ? "▍"
                                                        : ""}
                                                </pre>
                                            )}

                                            {m.role === "assistant" && !m.pending && (
                                                <div className="d-flex align-items-center gap-2 mt-2">
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className="border"
                                                        title={copiedIdx === i ? "Copied!" : "Copy"}
                                                        onClick={() => handleCopy(i)}
                                                    >
                                                        <FiCopy />
                                                    </Button>
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className="border"
                                                        title="Regenerate"
                                                        onClick={() => handleRegenerate(i)}
                                                    >
                                                        <FiRefreshCw />
                                                    </Button>
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className="border"
                                                        title="Good response"
                                                        onClick={() => console.log("thumbs up", i)}
                                                    >
                                                        <FiThumbsUp />
                                                    </Button>
                                                    <Button
                                                        variant="light"
                                                        size="sm"
                                                        className="border"
                                                        title="Needs improvement"
                                                        onClick={() => console.log("thumbs down", i)}
                                                    >
                                                        <FiThumbsDown />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Card.Body>
                <Card.Footer className="p-3" style={{ flex: "0 0 auto" }}>
                    <div className="position-relative">
                        <Form.Control
                            as="textarea"
                            rows={3}
                            className="chatBox_inputBox"
                            value={value + (interim ? (value ? " " : "") + interim : "")}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            style={{ paddingRight: "9.5rem", resize: "none" }}
                            placeholder={
                                isReady
                                    ? "Ask something about the embedded PDF..."
                                    : "Wait For Pdf Embedded Successfully."
                            }
                            disabled={inputDisabled}
                        />

                        {/* changed: give it a class 'chat-sendbar' and remove inline 'position-absolute' */}
                        <div className="chat-sendbar d-flex align-items-center gap-2 end-0 bottom-0 me-2 mb-2">
                            <button
                                variant="dark"
                                className="d-inline-flex align-items-center btn-chip"
                                title={isRecording ? "Stop listening" : "Speak to type"}
                                onClick={toggleMic}
                                disabled={inputDisabled}
                                style={isRecording ? { outline: "2px solid #d33" } : {}}
                            >
                                <FaMicrophone />
                                {isRecording ? <span className="ms-1">Listening...</span> : null}
                            </button>

                            <button
                                variant="dark"
                                className="d-inline-flex align-items-center btn-chip"
                                onClick={handleSend}
                                disabled={inputDisabled}
                                title="Send (Ctrl/Cmd + Enter)"
                            >
                                <FaPaperPlane />
                            </button>
                        </div>
                    </div>
                </Card.Footer>

            </Card>
        </div>
    );
}

export default Chat;

